import { inject, injectable } from 'inversify';
import { serializeError } from 'serialize-error';
import { CLIENT_EVENTS } from '../../constants/events';
import EnhancedEventEmitter from '../../EnhancedEventEmitter';
import InvalidPayloadError from '../../errors/InvalidPayloadError';
import { TOKEN } from '../../inversify.tokens';
import {
  AudioObserverEvents,
  ChannelAudioObserverEvents,
  DominantSpeakerEvent,
  PeersVolumesEvent,
} from '../../types/channelAudioObserver';
import { TrackLabel } from '../../types/common';
import Logger from '../Logger';
import Peer from '../Peer';
import PeersStorage from '../PeersStorage';
import ChannelAudioObserverMissingProducerRestorer from './ChannelAudioObserverMissingProducerRestorer';

@injectable()
class ChannelAudioObserverEventHandler {
  readonly #logger: Logger;

  #activePeerId?: Peer['id'];

  #currentActiveSpeaker?: Peer;

  #prevActiveSpeaker?: Peer;

  constructor(
    @inject(TOKEN.PeersStorage) private readonly peersStorage: PeersStorage,
    @inject(TOKEN.ClientEventEmitter) private readonly clientEventEmitter: EnhancedEventEmitter,
    @inject(TOKEN.ChannelAudioObserverMissingProducerRestorer)
    private readonly channelAudioObserverMissingProducerRestorer: ChannelAudioObserverMissingProducerRestorer,
    @inject(TOKEN.LoggerFactory) loggerFactory: (namespace: string) => Logger,
  ) {
    this.#logger = loggerFactory('ChannelAudioObserverEventHandler');

    this.watchPeersLeave();
  }

  public handle(data: string): void {
    try {
      const payload: AudioObserverEvents = JSON.parse(data);
      ChannelAudioObserverEventHandler.validateEventPayload(payload);

      switch (payload.event) {
        case ChannelAudioObserverEvents.DominantSpeaker: {
          this.handleDominantSpeaker(payload);
          break;
        }

        case ChannelAudioObserverEvents.PeersVolumes: {
          this.handlePeerVolumes(payload);
          break;
        }

        case ChannelAudioObserverEvents.Silence: {
          this.handleSilence();
          break;
        }
        default:
          break;
      }
    } catch (error) {
      this.#logger.warn('Failed to handle audio observer event', {
        error: serializeError(error),
        eventData: data,
      });
    }
  }

  private handleDominantSpeaker(payload: DominantSpeakerEvent): void {
    const { peerId } = payload.data;
    const peer = this.peersStorage.get(peerId);
    this.#currentActiveSpeaker = peer;
    this.#prevActiveSpeaker = peer;
    this.setActiveSpeaker(peer);
  }

  private handlePeerVolumes(payload: PeersVolumesEvent): void {
    payload.volumes.forEach((item) => {
      // this.channelAudioObserverMissingProducerRestorer.handlePeerVolume(item);
      const { peerId, trackLabel, value } = item;
      const peer = this.peersStorage.get(peerId);

      if (!peer) {
        return;
      }

      if (value > 0 && !this.#currentActiveSpeaker && peer.id === this.#prevActiveSpeaker?.id) {
        this.#currentActiveSpeaker = peer;
        this.setActiveSpeaker(peer);
      }

      const track = peer?.tracks.get(trackLabel);

      if (!track) {
        return;
      }

      track.setVolume(value);
    });
  }

  private handleSilence(): void {
    const tracks = this.peersStorage.all().flatMap((peer) => Array.from(peer.tracks.values()));
    tracks.forEach((track) => track.setVolume(0));
    this.#currentActiveSpeaker = undefined;
    this.setActiveSpeaker(undefined);
  }

  private watchPeersLeave(): void {
    this.clientEventEmitter.on(CLIENT_EVENTS.peerLeft, (peerId: Peer['id']): void => {
      if (peerId === this.#currentActiveSpeaker?.id) {
        this.setActiveSpeaker(undefined);
      }
    });
  }

  private setActiveSpeaker(peer?: Peer): void {
    if (this.#activePeerId === peer?.id) {
      return;
    }

    this.#activePeerId = peer?.id;
    this.clientEventEmitter.safeEmit(CLIENT_EVENTS.activeSpeakerChanged, { peer });
  }

  private static validateEventPayload(payload: AudioObserverEvents): void {
    switch (payload.event) {
      case ChannelAudioObserverEvents.Silence: {
        break;
      }

      case ChannelAudioObserverEvents.DominantSpeaker: {
        if (!payload.data) {
          ChannelAudioObserverEventHandler.throwInvalidPayload();
        }

        const { peerId, producerId, trackLabel } = payload.data;

        if (typeof peerId !== 'string' || typeof producerId !== 'string' || trackLabel !== TrackLabel.Microphone) {
          ChannelAudioObserverEventHandler.throwInvalidPayload();
        }
        break;
      }

      case ChannelAudioObserverEvents.PeersVolumes: {
        if (!Array.isArray(payload.volumes)) {
          ChannelAudioObserverEventHandler.throwInvalidPayload();
        }

        payload.volumes.forEach((item) => {
          const {
            peerId, producerId, value, trackLabel,
          } = item;

          if (
            typeof peerId !== 'string'
            || typeof producerId !== 'string'
            || typeof value !== 'number'
            || trackLabel !== TrackLabel.Microphone
          ) {
            ChannelAudioObserverEventHandler.throwInvalidPayload();
          }
        });
        break;
      }

      default: {
        ChannelAudioObserverEventHandler.throwInvalidPayload();
      }
    }
  }

  static throwInvalidPayload(): void {
    throw new InvalidPayloadError('invalid_channel_audio_observer_payload');
  }
}

export default ChannelAudioObserverEventHandler;
